import { mergeClasses, Switch } from "@fluentui/react-components";
import "leaflet/dist/leaflet.css";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { MapContainer, Pane, ZoomControl } from "react-leaflet";
import { LayoutContext } from "../../../context/LayoutContext";
import { useApiCall } from "../../../hooks/useApiCall";
import { useMapSas } from "../../../hooks/useMapSas";
import {
  IAirportMap,
  IFlightMarker,
} from "../../../models/airports/AirportConfig";
import { AIRPORT_CODE_PARAMETER } from "../../../utils/common/Constants";
import {
  mapApiUrl,
  MapErrorId,
  WEATHER_MAP,
  WORLD_BOUNDS,
} from "../../../utils/maps/MapConstants";
import { getAzureMapTileUrl } from "../../../utils/maps/MapHelperFunctions";
import DisplayMapErrors from "../DisplayMapErrors";
import AirportViewControls from "../map-controls/AirportViewControls";
import MapViewController from "../map-controls/MapViewController";
import AirportMarkers from "../map-markers/AirportMarkers";
import FlightMarker from "../map-markers/flight-markers/FlightMarker";
import SelectedFlightMarker from "../map-markers/flight-markers/SelectedFlightMarker";
import TileLayerWithHeader from "../TileLayerWithHeader";
import MapResources from "./MapResources";
import {
  flightsPaneControlsStyles,
  mapControlsStyles,
  mapDisplayErrorStyles,
  mapStyles,
} from "./mapStyles";

export interface IMapDisplayFlights {
  airportMapDetail: IAirportMap;
  setSideBarActive: (isActive: boolean) => void;
}

//Define the base URL and the endpoints
const flightTrackingEndPoint = process.env.REACT_APP_API_FLIGHT_TRACKING;
const baseURL = process.env.REACT_APP_API_BASE_URL;

const flightPositionsUrl = `${baseURL}${flightTrackingEndPoint}`;

const defaultFlightMarkerDetails: IFlightMarker = {
  id: "",
  docId: "",
  flightNumber: "",
  latitude: 0,
  longitude: 0,
  direction: 0,
  legType: "",
  status: "default",
  bestOffBlockTime: undefined,
  bestInBlockTime: undefined,
  callSign: undefined,
  aircraftType: "",
  aircraftRegistration: "",
};

const MapDisplayFlights: React.FC<IMapDisplayFlights> = ({
  airportMapDetail,
  setSideBarActive,
}) => {
  const mapStyleClass = mapStyles();
  const flightPaneClasses = flightsPaneControlsStyles();
  const viewControlsStyles = mapControlsStyles();
  const errorClasses = mapDisplayErrorStyles();

  let selectedFlightMarkerPosition: [number, number] | undefined = undefined;

  let selectedFlightMarkerDetails: IFlightMarker = {
    ...defaultFlightMarkerDetails,
  };

  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  const [showWeather, setShowWeather] = useState(true);
  const [showFlights, setShowFlights] = useState(true);
  const [showGates, setShowGates] = useState(false);
  const [showRSC, setShowRSC] = useState(false);
  const [mapApiFailures, setMapApiFailures] = useState<MapErrorId[]>([]);
  const [viewZoom, setViewZoom] = useState<number | null>(null);

  const { selectedFlight, flightFilterParams, setSelectedFlight } =
    useContext(LayoutContext);

  const {
    sas: mapSas,
    error: mapSasError,
    forceRefresh: refreshMapSas,
    needsReset: mapSasNeedsReset,
    acknowledgeReset: acknowledgeMapSasReset,
  } = useMapSas(airportMapDetail.id);
  const [tileLayerKey, setTileLayerKey] = useState(0);

  const azureBaseMapUrl = getAzureMapTileUrl(
    airportMapDetail.id,
    mapApiUrl,
    "BASE",
    mapSas
  );
  const azureWeatherMapUrl = getAzureMapTileUrl(
    airportMapDetail.id,
    mapApiUrl,
    "WEATHER",
    mapSas
  );

  let urlWithParams = flightPositionsUrl?.replace(
    AIRPORT_CODE_PARAMETER,
    airportMapDetail.id
  );
  urlWithParams += `?legType=${flightFilterParams.legType}`;
  if (flightFilterParams.flightIDs.length > 0) {
    urlWithParams += `&FlightNumbers=${flightFilterParams.flightIDs.join(",")}`;
  }

  const { data: flightData } = useApiCall<IFlightMarker[]>({
    url: urlWithParams,
    dependencies: [airportMapDetail.id, flightFilterParams],
    poll: 30,
  });

  const handleMarkerClick = useCallback(
    (flightID: string, docId: string) => {
      setSelectedFlight({ flightDocID: docId, flightID: flightID });
      setSideBarActive(true);
    },
    [setSelectedFlight, setSideBarActive]
  );

  const handleMapApiFailure = useCallback(
    (errorID: MapErrorId, isReset = false) => {
      if (isReset) {
        setMapApiFailures((prev) => prev.filter((id) => id !== errorID));
        return;
      }

      setMapApiFailures((prevFailures) =>
        prevFailures.includes(errorID)
          ? prevFailures
          : [...prevFailures, errorID]
      );
    },
    [setMapApiFailures]
  );

  useEffect(() => {
    handleMapApiFailure("SAS", !mapSasError);
  }, [mapSasError, handleMapApiFailure]);

  useEffect(() => {
    if (mapSasNeedsReset) {
      setTileLayerKey((prev) => prev + 1);
      acknowledgeMapSasReset();
    }
  }, [mapSasNeedsReset, acknowledgeMapSasReset]);

  const handleViewChange = useCallback((zoom: number) => {
    setViewZoom(zoom);
  }, []);

  const resetViewZoom = useCallback(() => {
    setViewZoom(null);
  }, []);

  const flightMarkers =
    flightData &&
    flightData?.map((flight: IFlightMarker) => {
      const selectedFlightMarker = flight.docId
        ? selectedFlight.flightDocID === flight.docId
        : flight.flightNumber
          ? selectedFlight.flightID === flight.flightNumber
          : false;

      if (selectedFlightMarker) {
        selectedFlightMarkerPosition = [flight.latitude, flight.longitude];
        selectedFlightMarkerDetails = { ...flight };
        return null;
      }

      return (
        <FlightMarker
          key={flight.id}
          airportCode={airportMapDetail.code}
          id={flight.id}
          flightNumber={flight.flightNumber}
          direction={flight.direction}
          position={[flight.latitude, flight.longitude]}
          flightStatus={flight.status}
          legType={flight.legType}
          docID={flight.docId}
          selectedFlight={selectedFlightMarker}
          pane="flights-pane"
          acType={flight.aircraftType}
          acReg={flight.aircraftRegistration}
          onBlockTime={flight.bestInBlockTime}
          offBlockTime={flight.bestOffBlockTime}
          updateMarkerId={handleMarkerClick}
        />
      );
    });

  useEffect(() => {
    if (mapRef.current && airportMapDetail?.location) {
      const map = mapRef.current;
      map.setView(airportMapDetail.location, map.getZoom());
    }
  }, [airportMapDetail?.location]);

  useEffect(() => {
    if (mapRef.current && selectedFlightMarkerPosition) {
      mapRef?.current.flyTo(
        selectedFlightMarkerPosition,
        mapRef.current.getZoom(),
        {
          animate: true,
          duration: 1.2,
        }
      );
    }
  }, [selectedFlight.flightDocID, selectedFlight.flightID]);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!mapRef.current) {
        return;
      }
      const map = mapRef.current;
      const center = map.getCenter();

      requestAnimationFrame(() => {
        map.invalidateSize({ animate: false });
        map.setView(center, map.getZoom(), { animate: false });
      });
    });

    resizeObserver.observe(mapContainerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <section
      ref={mapContainerRef}
      className={`${mapStyleClass.mapContainer} ${
        showFlights
          ? flightPaneClasses.flightsPaneVisible
          : flightPaneClasses.flightsPaneHidden
      }`}
      data-testid="flight-tracking-map"
    >
      <section
        className={mergeClasses(mapStyleClass.mapControlContainer)}
        data-testid="map-controls"
      >
        <div
          className={mergeClasses(
            viewControlsStyles.cardContainer,
            viewControlsStyles.toggleControls
          )}
        >
          <Switch
            checked={showWeather}
            onChange={(_e, data) => setShowWeather(data.checked)}
            label="Precipitation"
          />

          <Switch
            checked={showFlights}
            onChange={(_e, data) => setShowFlights(data.checked)}
            label="Flights"
          />

          <Switch
            checked={showGates}
            onChange={(_e, data) => setShowGates(data.checked)}
            label="Stands"
          />

          <Switch
            checked={showRSC}
            onChange={(_e, data) => setShowRSC(data.checked)}
            label="RSC"
          />
        </div>

        <AirportViewControls onViewChange={handleViewChange} />
      </section>

      <MapContainer
        center={airportMapDetail.location}
        zoom={7}
        style={{
          height: "100%",
          width: "100%",
          borderRadius: "8px",
          fontSize: "inherit",
          fontFamily: "inherit",
        }}
        zoomControl={false}
        minZoom={4}
        maxZoom={17}
        ref={mapRef}
        preferCanvas={false}
        maxBounds={WORLD_BOUNDS}
        attributionControl={true}
        maxBoundsViscosity={0.8}
      >
        <MapViewController
          center={airportMapDetail.location}
          zoom={viewZoom}
          onViewApplied={resetViewZoom}
        />
        <ZoomControl position="bottomright" />
        {mapSas && (
          <TileLayerWithHeader
            key={`BASE-${tileLayerKey}`}
            id="BASE"
            url={azureBaseMapUrl}
            attribution="© Microsoft Azure Maps"
            layerOpacity={0.9}
            zIndex={100}
            keepBuffer={2}
            updateInterval={1000}
            updateWhenZooming={false}
            updateWhenIdle={true}
            setMapApiFailures={handleMapApiFailure}
            onAuthError={refreshMapSas}
          />
        )}

        {showWeather && mapSas && (
          <TileLayerWithHeader
            key={`WEATHER-${tileLayerKey}`}
            id="WEATHER"
            url={azureWeatherMapUrl}
            attribution="© Microsoft Azure Maps"
            zIndex={200}
            keepBuffer={3}
            refreshInterval={1000 * 60 * 5} // 5 minutes
            maxNativeZoom={WEATHER_MAP.maxNativeZoom}
            updateInterval={500}
            layerOpacity={0.8}
            updateWhenZooming={false}
            updateWhenIdle={true}
            setMapApiFailures={handleMapApiFailure}
            onAuthError={refreshMapSas}
          />
        )}

        <AirportMarkers
          key={airportMapDetail.id}
          code={airportMapDetail.code}
          name={airportMapDetail.name}
          position={airportMapDetail.location}
          handleMarkerClick={handleViewChange}
        />

        <Pane name="flight-tooltip-pane" style={{ zIndex: 800 }} />

        <Pane name="flights-pane">
          {flightMarkers}
          {selectedFlightMarkerDetails.id && (
            <SelectedFlightMarker
              selectedFlightMarkerDetails={selectedFlightMarkerDetails}
              airportCode={airportMapDetail.code}
              pane="flights-pane"
              handleMarkerClick={handleMarkerClick}
            />
          )}
        </Pane>

        <MapResources
          airportId={airportMapDetail.id}
          showGates={showGates}
          showRSC={showRSC}
          setMapApiFailures={handleMapApiFailure}
        />

        <DisplayMapErrors
          messages={mapApiFailures}
          className={errorClasses.flightMap}
        />
      </MapContainer>
    </section>
  );
};

export default MapDisplayFlights;
